import {
  createExitEditRoute,
  createInvestmentViewLink,
} from 'adminConstants/routes';
import { NoOfSharesHistoryType } from 'further-types/shareholding';
import { dateToLabel, numberToDisplayString } from 'further-ui/utils';
import {
  CallSplit,
  CompareArrows,
  SettingsEthernet,
} from '@mui/icons-material';
import { Box, Tooltip } from '@mui/material';
import { Link } from 'react-router-dom';
import { sanitiseHtml } from 'further-ui/utils';
import api from 'lib/trpcClient';

type Shareholding = {
  isTransferred?: boolean;
  noOfSharesHistory?: Array<{
    date: string;
    noOfShares: number;
    setVia?: NoOfSharesHistoryType;
  }>;
  sourceShareholding?: Shareholding;
  sharesCurrentlyHeld: number;
  investor?: {
    fullName: string;
  };
  investmentId?: string;
  createdAt?: string;
  transferDate?: string;
  fullName?: string;
  transferredShareholdings?: Array<Shareholding>;
  noOfShare?: number;
  exitId?: string;
  noOfSharesExited?: number;
  investmentDate?: string;
};

const getInitialTransferredSharesAmount = (shareholding: Shareholding) => {
  return (
    shareholding.noOfSharesHistory?.[0]?.noOfShares ??
    shareholding.sharesCurrentlyHeld ??
    shareholding.noOfShare ??
    0
  );
};

const getSharesTransferred = (shareholding: Shareholding) => {
  const sharesAmount = getInitialTransferredSharesAmount(shareholding);
  return `${numberToDisplayString(sharesAmount)} ${
    sharesAmount === 1 ? 'share was' : 'shares were'
  } transferred`;
};

const ShareholdingLabel: React.FC<{
  label: string;
  labelHref?: string;
  shareholding: Shareholding;
  transferDate?: string;
  classes: Record<string, string>;
  showTooltipForTransferredShareholdings?: boolean;
}> = ({
  label,
  labelHref,
  shareholding,
  transferDate,
  classes,
  showTooltipForTransferredShareholdings = true,
}) => {
  const previousShareSplits =
    shareholding.noOfSharesHistory?.filter(
      (item) => item.setVia === NoOfSharesHistoryType.ShareSplit,
    ) || [];

  const isPartOfTransfer =
    !!shareholding.sourceShareholding ||
    shareholding.isTransferred ||
    (showTooltipForTransferredShareholdings &&
      shareholding.transferredShareholdings?.length);

  const isShareConsideration = !!shareholding.exitId;

  // Fetch exit information for share consideration shareholdings
  const { data: exitData } = api.exit.getById.useQuery(
    { id: shareholding.exitId! },
    {
      enabled: isShareConsideration && !!shareholding.exitId,
    },
  );

  const labelJsx = labelHref ? (
    <Link className={classes.link} to={labelHref}>
      {label}
    </Link>
  ) : (
    label
  );
  if (
    !previousShareSplits?.length &&
    !isPartOfTransfer &&
    !isShareConsideration
  )
    return <div className={classes.mediumColumn}>{labelJsx}</div>;

  const shareSplitDates = previousShareSplits
    .map((item) => dateToLabel(item.date))
    .join(' and ');

  // Get share consideration tooltip text
  const getShareConsiderationTooltip = () => {
    const exit = exitData;

    if (!exit) {
      return '';
    }

    // Use the shareholding's investmentDate instead of exit.date
    // This ensures we show the date of the shareholding itself, not the latest amendment date
    const shareholdingDate = shareholding.investmentDate
      ? dateToLabel(shareholding.investmentDate)
      : '';
    const companies = exit.companies || [];

    // Get total shares exited for this investor/shareholding
    const totalSharesExited = shareholding.noOfSharesExited || 0;

    // Get company name - use tradingName from first company
    const companyName =
      companies.length > 0 ? companies[0]?.tradingName || '' : '';

    if (!companyName || !shareholdingDate) {
      return '';
    }

    const exitEditLink = createExitEditRoute(shareholding.exitId ?? '');

    if (totalSharesExited === 0) {
      return `These shares were received as free consideration for the exit of <a class="${classes.tooltipLink}" style="color: #4caf50;" href="${exitEditLink}" target="_blank">${companyName} on ${shareholdingDate}</a>.`;
    }

    return `These shares were received in consideration for the sale of ${numberToDisplayString(
      totalSharesExited,
    )} shares in <a class="${
      classes.tooltipLink
    }" style="color: #4caf50;" href="${exitEditLink}" target="_blank">${companyName} on ${shareholdingDate}</a>.`;
  };

  const tooltipText = [
    ...(shareSplitDates?.length
      ? [`This company was subject to a share split on ${shareSplitDates}. `]
      : []),
    ...(shareholding.sourceShareholding
      ? [
          `${getSharesTransferred(shareholding)} from <a class="${
            classes.tooltipLink
          }" href="${createInvestmentViewLink(
            shareholding.sourceShareholding.investmentId ?? '',
          )}" target="_blank">${
            shareholding.sourceShareholding?.investor?.fullName
          }</a> on ${dateToLabel(transferDate || shareholding.createdAt)}.`,
        ]
      : []),
    ...(shareholding.isTransferred
      ? [
          `${getSharesTransferred(shareholding)} to <a class="${
            classes.tooltipLink
          }" href="${createInvestmentViewLink(
            shareholding.investmentId ?? '',
          )}" target="_blank">${
            shareholding.investor?.fullName
          }</a> on ${dateToLabel(shareholding.transferDate)}.`,
        ]
      : []),
    ...(showTooltipForTransferredShareholdings &&
    shareholding.transferredShareholdings?.length
      ? shareholding.transferredShareholdings.map(
          (transferredShareholding, i) => [
            `${getSharesTransferred(transferredShareholding)} to <a class="${
              classes.tooltipLink
            }" href="${createInvestmentViewLink(
              transferredShareholding.investmentId ?? '',
            )}" target="_blank">${
              transferredShareholding.investor?.fullName
            }</a> on ${dateToLabel(
              transferredShareholding.transferDate ||
                transferredShareholding.createdAt,
            )}. </br>${
              i === (shareholding.transferredShareholdings?.length ?? 0) - 1
                ? ''
                : '<br />'
            }`,
          ],
        )
      : []),
    ...(isShareConsideration ? [getShareConsiderationTooltip()] : []),
  ].join(' ');

  const icons = [
    ...(shareSplitDates ? [CallSplit] : []),
    ...(isPartOfTransfer ? [CompareArrows] : []),
    ...(isShareConsideration ? [SettingsEthernet] : []),
  ];

  return (
    <div className={classes.mediumColumn}>
      <Tooltip
        title={
          <div
            dangerouslySetInnerHTML={{
              __html: sanitiseHtml(tooltipText) ?? '',
            }}
          />
        }
      >
        <Box alignItems="center" display="flex" gap={2}>
          {labelJsx}{' '}
          {icons.map((Icon, i) => (
            <Icon key={i} className={classes.splitIcon} />
          ))}
        </Box>
      </Tooltip>
    </div>
  );
};

export default ShareholdingLabel;
